/**
 * The purchase a visitor had already decided on, carried across the sign-in detour.
 *
 * ## The seam this closes
 *
 * Both places a person can start paying — `PricingScreen.handleSelect` and
 * `WatchSetupScreen.startCheckout` — do the same correct thing when the caller
 * is anonymous or on the shared demo identity:
 *
 *     if (!user || user.isDemo) { navigate("Auth"); return; }
 *
 * That guard is right (the server answers `403 demo_read_only`, and attaching a
 * buyer's card to `demo-user-001` would be worse). But nothing recorded WHAT
 * they were buying. `AuthScreen` has no navigation prop — the navigator simply
 * re-renders once `user` exists — so after signing in they land on the
 * dashboard, and the tier and billing period they had chosen are gone. To pay
 * us they must find Pricing again, re-pick the plan, re-toggle annual, and
 * click a second time.
 *
 * The funnel says this is where people stop: `watch.signup_required` fired and
 * no checkout event ever followed it.
 *
 * `watchIntent.ts` already does exactly this for watchlist symbols. This is the
 * same idea for the thing that actually earns money, and deliberately mirrors
 * its shape — localStorage, synchronous, non-throwing.
 *
 * ## Why it expires, and why it is consumed rather than read
 *
 * A stored purchase intent is a loaded gun pointed at a future session. Someone
 * who abandoned checkout on Tuesday must not be met with "Continue to Elite —
 * $299/mo" on Friday. It lives 15 minutes: long enough to sign up, verify an
 * email, and come back; too short to ambush anyone.
 *
 * `consumePendingCheckoutIntent()` clears BEFORE the caller acts on it. Clearing
 * after a successful checkout instead would loop forever whenever checkout
 * errors — which is the one case where the user is already unhappy.
 */

const STORAGE_KEY = "fahali_pending_checkout_v1";

/** Long enough to sign up and return; short enough not to ambush a later visit. */
export const CHECKOUT_INTENT_TTL_MS = 15 * 60 * 1000;

export type BillingPeriod = "monthly" | "annual";

export interface PendingCheckoutIntent {
  tier: string;
  billingPeriod: BillingPeriod;
  /** Where the click came from — for funnel attribution, never for logic. */
  source: string;
  capturedAt: string;
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Expired or malformed both mean "no intent" — never a partially-trusted one. */
function parse(raw: string | null, now: number): PendingCheckoutIntent | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PendingCheckoutIntent>;
    const tier = typeof p.tier === "string" ? p.tier.trim().toLowerCase() : "";
    if (!tier || tier.length > 32) return null;

    const at = Date.parse(String(p.capturedAt || ""));
    if (!Number.isFinite(at)) return null;
    if (now - at > CHECKOUT_INTENT_TTL_MS) return null;
    // A clock that moved backwards (timezone change, NTP correction) must not
    // produce an intent that looks fresh forever.
    if (at - now > 60_000) return null;

    return {
      tier,
      billingPeriod: p.billingPeriod === "annual" ? "annual" : "monthly",
      source: String(p.source || "pricing").slice(0, 64),
      capturedAt: new Date(at).toISOString(),
    };
  } catch {
    return null;
  }
}

export function setPendingCheckoutIntent(
  tier: string,
  billingPeriod: BillingPeriod,
  source: string = "pricing",
): PendingCheckoutIntent | null {
  const clean = String(tier || "").trim().toLowerCase();
  if (!clean) return null;
  const intent: PendingCheckoutIntent = {
    tier: clean,
    billingPeriod: billingPeriod === "annual" ? "annual" : "monthly",
    source,
    capturedAt: new Date().toISOString(),
  };
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // A full or disabled localStorage must never block someone from signing in.
  }
  return intent;
}

/** Non-destructive look — use this only to decide where to send someone. */
export function readPendingCheckoutIntent(now: number = Date.now()): PendingCheckoutIntent | null {
  try {
    return parse(storage()?.getItem(STORAGE_KEY) ?? null, now);
  } catch {
    return null;
  }
}

export function clearPendingCheckoutIntent(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Read and clear in one step. Always clear, even when the stored value was
 * expired or unparseable — leaving a rejected value behind means every later
 * read pays to reject it again.
 */
export function consumePendingCheckoutIntent(
  now: number = Date.now(),
): PendingCheckoutIntent | null {
  const intent = readPendingCheckoutIntent(now);
  clearPendingCheckoutIntent();
  return intent;
}
